import { Component, OnInit, ViewEncapsulation, ViewChild, Input, OnDestroy } from '@angular/core';
import { HelpIndex, UpdateStatusType, Endpoint, MessageType, LoadFileType, ConnectorType } from '../../../models/enums';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { GenericColDef, GridRowSelectionType, GridContext, FilterTemplateType, AlignDirection } from '../../../models/grid-context';
import { SelectionChangedEvent } from 'ag-grid-community';
import { Connector } from '../../../models/connector';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../../generic-tree/generic-tree.component';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { NewDataSetLoadFileMenuComponent } from '../../new-data-set-load-file-menu/new-data-set-load-file-menu.component';
import * as _ from 'underscore';
import { isUndefined } from '../../../models/utils';
import { DrObject } from '../../../models/dr-object';
import { ComboBoxCellComponent } from '../../grid-templates/combo-box-cell/combo-box-cell.component';
import { cloneDeep } from 'lodash';
import { ResizerComponent } from '../../resizer/resizer.component';
import { SmfType } from '../../../models/corpus';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { IndexLevel } from '../../../models/index-settings';
import { FilterComponent } from '../../filter/filter.component';

@Component({
    standalone: false,
    selector: 'res-data-set-modal',
    templateUrl: './data-set-modal.component.html',
    styleUrls: ['./data-set-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DataSetModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
    @ViewChild(GenericTreeComponent, { static: true }) tree: GenericTreeComponent;
    @ViewChild('connectorGrid', { static: true }) connectorGrid: GenericGridComponent;
    @ViewChild('custodianGrid', { static: true }) custodianGrid: GenericGridComponent;
    @ViewChild('googleCustodianGrid', { static: true }) googleCustodianGrid: GenericGridComponent;
    @ViewChild('mediaIdGrid', { static: true }) mediaIDGrid: GenericGridComponent;
    @ViewChild(NewDataSetLoadFileMenuComponent, { static: true }) menuComponent: NewDataSetLoadFileMenuComponent;
    @ViewChild('treeLeftResizer', { static: true }) treeLeftResizer: ResizerComponent;
    selectedConnector: Connector = null;
    @ViewChild('resFilterComponent') resFilterComponent!: FilterComponent;
    nodes = new Array<GenericTreeNode>();
    dataAreaNodes = new Array<any>();
    selectedDataAreaPaths = [];
    projectCustodians = [];
    projectMediaIds = [];
    readonly IndexLevel = IndexLevel;
    debounceFilter = _.debounce(this.filterAfterBounce, 1000);

    // modal information
    modalTitle = 'New Data Set';
    modalAutomationId = 'new-data-set';
    verifyRequiredValidator = true;
    primaryButtonLabel = 'Create Data Set';
    secondaryButtonLabel = 'Cancel';
    isBusy = false;
    modalHelpIndex = HelpIndex.imports_add_data_to_case_dlg;
    isBusyMessage = 'Loading ...';
    modalClass = 'new-data-set';

    // grid information
    gridName = 'Connectors';
    gridAutomationId = 'new-data-set';
    updateStatusType = UpdateStatusType.CONNECTOR;
    isBusyGrid = false;
    isGridBusyMessage = 'Loading ...';
    isConnectorSelected = false;
    isGoogleConnectorSelected = false;
    inputAutoFocused = true;
    isFiltering = false;

    // tree information
    isPathsBusy = false;
    parentNodeName = '';
    custodiansAndMediaIDs = {
        fieldNames: {
            custodian: 'custodian',
            mediaId: 'mediaId'
        },
        isBusy: {
            custodian: false,
            mediaId: false
        },
        isError: {
            custodian: false,
            mediaId: false
        },
        items: {
            custodian: [],
            mediaId: []
        }
    }

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Connector Name',
            field: 'name',
            sortBy: 'name',
            automationId: this.gridAutomationId,
            lockVisible: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.gridAutomationId
        },
        {
            headerName: 'Type',
            field: 'type',
            automationId: this.gridAutomationId,
            width: 110,
            suppressSizeToFit: true
        },
        {
            headerName: 'Mode',
            field: 'modeDisplay',
            automationId: this.gridAutomationId,
            width: 95,
            suppressSizeToFit: true
        },
        {
            headerName: 'Server',
            field: 'remoteHost',
            automationId: this.gridAutomationId
        },
        {
            headerName: 'Path',
            field: 'remotePath',
            automationId: this.gridAutomationId
        }
    );
    custodianColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Source Custodian',
            field: this.custodiansAndMediaIDs.fieldNames.custodian,
            automationId: this.modalAutomationId + '-custodian',
            filterType: FilterTemplateType.freeform,
            lockVisible: true,
            suppressMovable: true,
            headerAlignDirection: AlignDirection.left,
            cellAlignDirection: AlignDirection.left,
            cellRenderer: 'gridCellRenderer',
        },
        {
            headerName: 'Project Custodian',
            field: 'newValue',
            automationId: this.modalAutomationId + '-custodian',
            filterType: FilterTemplateType.comboBox,
            lockVisible: true,
            suppressMovable: true,
            minWidth: 140,
            cellRenderer: ComboBoxCellComponent,
            cellRendererParams: {
                onChange: this.onCustodianValueChange.bind(this),
                field: this.custodiansAndMediaIDs.fieldNames.custodian,
                itemList: this.projectCustodians,
                hasLabelsDisplayed: true
            },
            suppressKeyboardEvent: () => true
        }
    );
    googleCustodianColumnDefs = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            checkboxSelection: true, headerCheckboxSelection: true,
            minWidth: 36, maxWidth: 36, width: 36,
            suppressSizeToFit: true, suppressMovable: true,
            automationId: this.modalAutomationId + '-custodian',
            cellRenderer: 'gridCellRenderer'
        },
        {
            headerName: 'Source Account',
            field: 'sourceAccount',
            automationId: this.modalAutomationId + '-custodian',
            filterType: FilterTemplateType.freeform,
            lockVisible: true,
            suppressMovable: true,
            headerAlignDirection: AlignDirection.left,
            cellAlignDirection: AlignDirection.left,
            cellRenderer: 'gridCellRenderer',
        },
        {
            headerName: 'Project Custodian',
            field: 'newValue',
            automationId: this.modalAutomationId + '-custodian',
            filterType: FilterTemplateType.comboBox,
            lockVisible: true,
            suppressMovable: true,
            minWidth: 140,
            cellRenderer: ComboBoxCellComponent,
            cellRendererParams: {
                onChange: this.onCustodianValueChange.bind(this),
                field: this.custodiansAndMediaIDs.fieldNames.custodian,
                itemList: this.projectCustodians,
                hasLabelsDisplayed: true
            },
            suppressKeyboardEvent: () => true
        }
    );

    mediaIDColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Source MediaID',
            field: this.custodiansAndMediaIDs.fieldNames.mediaId,
            automationId: this.modalAutomationId + '-mediaId',
            filterType: FilterTemplateType.freeform,
            lockVisible: true,
            suppressMovable: true,
            headerAlignDirection: AlignDirection.left,
            cellAlignDirection: AlignDirection.left,
            cellRenderer: 'gridCellRenderer',
        },
        {
            headerName: 'Project MediaID',
            field: 'newValue',
            automationId: this.modalAutomationId + '-mediaId',
            filterType: FilterTemplateType.comboBox,
            lockVisible: true,
            suppressMovable: true,
            minWidth: 140,
            cellRenderer: ComboBoxCellComponent,
            cellRendererParams: {
                onChange: this.onMediaIdValueChange.bind(this),
                field: this.custodiansAndMediaIDs.fieldNames.mediaId,
                itemList: this.projectMediaIds,
                hasLabelsDisplayed: true
            },
            suppressKeyboardEvent: () => true
        }
    );

    connectorsGridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true,
    });

    gridContext = (isCustodian: boolean) => new GridContext({
        loadAndFilter: true,
        inlineGridFilter: true,
        onFilterApply: (queryStr: string, filterType: any, headerName: any) => isCustodian ?
            this.onFilterApply(queryStr, filterType, headerName, this.custodiansAndMediaIDs.fieldNames.custodian) :
            this.onFilterApply(queryStr, filterType, headerName, this.custodiansAndMediaIDs.fieldNames.mediaId),
    });

    connectorSelection = GridRowSelectionType.singleCheckboxWithRowClickSelection;
    hasAddToProjectPermissions = true;
    addToProjectData = false;

    requestInfo = {
        endpoint: Endpoint.adminOrgManager_listConnectors,
        responseName: 'connectors',
        data: {
            organizationName: this.commonService?.currentUser?.customerName
        }
    };

    constructor(
        private modalService: BsModalService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
        private restService: RestService
    ) {
        super(modalService, modalRef);
    }
  
    ngOnInit(): void {
        if (this.modalData.shortMessageFormat) {
            this.modalTitle = 'New Data Set for Short Message Format';
            this.modalHelpIndex = HelpIndex.imports_add_ds_short_message_format_dlg;
        }
        this.hasAddToProjectPermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] }]);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    ngOnDestroy(): void {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape' && this.isFiltering) {
            event.stopPropagation();
            event.preventDefault();
            this.filterData('');
        }
    }

     responseCallback = (response: any) => {
        if (response?.connectors) {
            const connectors = new Array<Connector>();
            response.connectors.forEach(element => {
                if (!this.modalData.shortMessageFormat || (this.modalData.shortMessageFormat && element.type !== ConnectorType.GoogleWorkspace)) {
                    const connector = new Connector(element);
                    connectors.push(connector);
                }
            });
            response.connectors = connectors;
        }
    }

    connectorOnSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api) {
            this.clearGrids();
            this.selectedConnector = event.api.getSelectedRows()[0];
            const remotePath = this.selectedConnector && this.selectedConnector.remotePath ? this.selectedConnector.remotePath : '';
            this.parentNodeName = remotePath.substring(remotePath.lastIndexOf('/') + 1);            
            if (this.selectedConnector) {
                this.isConnectorSelected = true;
                const pathParam = { name: '', handle: '', type: 'UNKNOWN', leaf: false };
                this.nodes = [];
                const colDef = this.mediaIDColumnDefs.find(f => f.cellRenderer === 'gridCellRenderer');
                if (this.selectedConnector.type === ConnectorType.GoogleWorkspace.toString()) {
                    this.isGoogleConnectorSelected = true;
                     if (colDef) {
                        colDef.field = 'dataSource';
                        colDef.headerName = 'Source Service';
                        if (this.mediaIDGrid?.gridApi) {
                            this.mediaIDGrid?.gridApi.setGridOption('columnDefs', this.mediaIDColumnDefs);
                        }
                    }
                    this.loadAccountsAndServices();
                    this.updateResizer(); 
                } else {
                    this.isGoogleConnectorSelected = false;
                    if (colDef) {
                        colDef.field = this.custodiansAndMediaIDs.fieldNames.mediaId;
                        colDef.headerName = 'Source MediaID';
                        if (this.mediaIDGrid?.gridApi) {
                            this.mediaIDGrid?.gridApi.setGridOption('columnDefs', this.mediaIDColumnDefs);
                        }
                    }
                    this.loadPaths(pathParam); 
                    this.commonService.sendMessage(MessageType.connectorChanged, {
                        connector: this.selectedConnector, paths: this.selectedDataAreaPaths,
        });
                }
            } else {
                this.isConnectorSelected = false;
            }
        }
    }

    custodianOnSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api) {
            this.dataAreaNodes = [];
            const custodians = event.api.getSelectedRows()
            custodians.forEach(custodian => {
                this.dataAreaNodes.push({
                    dataAreaPath: custodian.sourceAccount,
                    skippedDirectories: [],
                    allDescendants: [],
                    name: custodian.newValue
                });
            });
            this.googleCustodianGrid.getRows().forEach(row => {
                if (row.isSelected()) {
                    row.data.isDis = false;
                } else {
                    row.data.isDis = true;
                }
            })
            this.mediaIDGrid?.setData([]);
            this.populateMediaIDGridForGoogleWorkspace(null);
        }
    }


    isDescendant(node) {
        this.dataAreaNodes.forEach(dataArea => {
            const index = dataArea.allDescendants.indexOf(node.data.handle);

            if (index !== -1) {
                dataArea.allDescendants.splice(index, 1);
                if (dataArea.skippedDirectories.indexOf(node.data.handle) === -1) {
                    dataArea.skippedDirectories.push(node.data.handle);
                }
            }
        });
    }

    onCustodianValueChange(element) {
        this.onValueChange(element, this.custodiansAndMediaIDs.fieldNames.custodian);
    }

    onMediaIdValueChange(element) {
        this.onValueChange(element, this.custodiansAndMediaIDs.fieldNames.mediaId);
    }

    onValueChange(el, field) {
        let isError = false;
        const foundObject = _.find(this.custodiansAndMediaIDs.items[field], cust => cust[field] === el.data[field]);
        if (foundObject) {
            foundObject.newValue = el.data.newValue;
            foundObject.popoverError = el.data.popoverError;
        }
        this.custodiansAndMediaIDs.items[field].forEach(element => {
            if (element.popoverError.isError) {
                isError = true;
            }
        })
        this.custodiansAndMediaIDs.isError[field] = isError;
    }

    checkboxSelectionChangedCallback = (selectedNodes: Array<GenericTreeNode>, selectedNode: GenericTreeNode) => {
        if (selectedNodes) {
            if (this.selectedConnector.type === ConnectorType.GoogleWorkspace.toString()) {
                this.populateMediaIDGridForGoogleWorkspace(selectedNode);
            } else {
                const isChecked = selectedNodes.includes(selectedNode);
                if (isChecked) {
                    const allDescendants = this.tree.treeControl.getDescendants(selectedNode).filter(n => n.hasCheckbox);
                    const nodes = [selectedNode].concat(allDescendants);
                    nodes.forEach(node => {
                        node.data.isDataArea = this.nodeIsDataArea(node);

                        // populate the name field on a data area node selection
                        if (node.data.isDataArea && (!this.menuComponent.nameDescriptionComponent.isNameChanged)) {
                            this.menuComponent.nameDescriptionComponent.name = node.data.name ? node.data.name : node.name;
                            this.menuComponent.nameDescriptionComponent.isNameChanged = false;
                            this.menuComponent.nameDescriptionComponent.onFocus();
                        }

                        const isInDataAreaNodes = _.findWhere(this.dataAreaNodes, { dataAreaPath: node.data.handle });
                        const index = this.dataAreaNodes.indexOf(isInDataAreaNodes);
                        if (node.data.isDataArea && index === -1) {
                            this.dataAreaNodes.push({
                                dataAreaPath: node.data.handle,
                                skippedDirectories: [],
                                allDescendants: _.map(allDescendants, function (n) { return n.data.handle; }),
                                name: node.data.name
                            });
                        } else if (node.data.isDataArea && index !== -1) {
                            if (this.tree.childrenAllSelected(node)) {
                                const allChildren = [];
                                node.children.forEach(child => {
                                    allChildren.push(child.data.handle);
                                });
                                this.dataAreaNodes[index].skippedDirectories = [];
                                this.dataAreaNodes[index].allDescendants = [];
                                this.dataAreaNodes[index].allDescendants = allChildren;
                            }
                        } else {
                            this.dataAreaNodes.forEach(dataArea => {
                                dataArea.skippedDirectories.forEach(skippedDirectory => {
                                    if (skippedDirectory === node.data.handle) {
                                        const indexofSkippedDirectory = dataArea.skippedDirectories.indexOf(skippedDirectory);
                                        dataArea.skippedDirectories.splice(indexofSkippedDirectory, 1);
                                        if (dataArea.allDescendants.indexOf(skippedDirectory) === -1) {
                                            dataArea.allDescendants.push(skippedDirectory);
                                        }
                                    }
                                });
                            });
                        }
                    });
                } else {
                    if (selectedNode.data.isDataArea && (!this.menuComponent.nameDescriptionComponent.isNameChanged) &&
                        this.menuComponent.nameDescriptionComponent.name === (selectedNode.data.name ? selectedNode.data.name : selectedNode.name)) {
                        this.menuComponent.nameDescriptionComponent.name = '';
                        this.menuComponent.nameDescriptionComponent.isNameChanged = false;
                        this.menuComponent.nameDescriptionComponent.onFocus();
                    }
                    const nodes = [selectedNode].concat(this.tree.treeControl.getDescendants(selectedNode).filter(n => n.hasCheckbox));
                    nodes.forEach(node => {
                        node.data.isDataArea = false;

                        const findPathInDataAreaNode = _.findWhere(this.dataAreaNodes, { dataAreaPath: node.data.handle });
                        const indexInDataAreaNodes = this.dataAreaNodes.indexOf(findPathInDataAreaNode);

                        if (indexInDataAreaNodes !== -1) {
                            this.dataAreaNodes.splice(indexInDataAreaNodes, 1);
                        } else {
                            this.isDescendant(node);
                        }
                    });
                }
                this.refreshCustodianMediaIdGrids();
                this.commonService.sendMessage(MessageType.connectorChanged, {
                    connector: this.selectedConnector, paths: this.selectedDataAreaPaths,
                });
            }
        }
    }

    populateMediaIDGridForGoogleWorkspace(selectedNode: GenericTreeNode) {
        let selectedCustodianRows = this.googleCustodianGrid.gridApi?.getSelectedRows();
        if (selectedNode) {
            this.addMediaIDValuesForNode(selectedNode);
        } else {
            if (this.tree.data.length > 0) {
                this.tree.selectedNodes.forEach(node => this.addMediaIDValuesForNode(node));
            }
        }
    }

    addMediaIDValuesForNode(selectedNode: GenericTreeNode) {
        this.googleCustodianGrid.gridApi.forEachNode(custodianRow => {
            let value = '';
            const index = this.custodiansAndMediaIDs.items.mediaId.findIndex(mediaIdObj => mediaIdObj.dataSource === selectedNode.name && mediaIdObj.sourceAccount === custodianRow.data.sourceAccount);
            const emailParts = custodianRow.data.custodian.split(' ');
            let firstSectionName = '';
            if (emailParts?.length > 1) {                
                firstSectionName = emailParts[0].substring(0, 1).toLowerCase() + emailParts[1].toLowerCase();             
            }
            firstSectionName += '_' + selectedNode.name.toLowerCase() + '_';
            if (selectedNode.isChecked && custodianRow.isSelected()) {
                if (index < 0) {
                    let similarCustodians = this.projectMediaIds.sort().filter(projCustodian => {
                        return projCustodian.indexOf(firstSectionName) >= 0
                    })
                    if (similarCustodians?.length > 0) {
                        let lastCustodianSegments = similarCustodians[similarCustodians.length - 1].split('_');
                        let lastNumber = parseInt(lastCustodianSegments[lastCustodianSegments.length - 1]);
                        let newNumberString = (lastNumber + 1).toString();
                        value = newNumberString.length === 3 ? newNumberString : newNumberString.length === 2 ? '0' + newNumberString : '00' + newNumberString;
                    } else {
                        value = '001';
                    }
                    let mediaId = {
                        dataSource: selectedNode.name,
                        sourceAccount: custodianRow.data.sourceAccount,
                        mediaId: firstSectionName + value,
                        newValue: firstSectionName + value,
                        popoverError: {
                            isError: false,
                            popover: false,
                            message: '',
                            type: 'error'
                        }
                    };
                    this.custodiansAndMediaIDs.items.mediaId.push(mediaId);
                }
            } else {
                if (index > -1) {
                    this.custodiansAndMediaIDs.items.mediaId.splice(index, 1);
                }
            }
        })
        this.mediaIDGrid.setData(this.custodiansAndMediaIDs.items.mediaId);
    }


    nodeIsDataArea = (node: GenericTreeNode) => {
        if (node.parent && node.parent.data) {
            if (node.parent.data.isDataArea) {
                return false;
            } else {
                return this.nodeIsDataArea(node.parent);
            }
        } else {
            return true;
        }
    }

    nodeAlternativeIconCallback = (node: GenericTreeNode): string => {
        if (node && node.data && node.data.isDataArea) {
            return 'fa-database color-blue-01';
        }
    }

    clearGrids() {
        this.googleCustodianGrid?.gridContext?.clearAllFilterInputs.emit();
        this.googleCustodianGrid?.setData([]);
        this.custodianGrid?.gridContext?.clearAllFilterInputs.emit();
        this.custodianGrid?.setData([]);
        this.mediaIDGrid?.gridContext?.clearAllFilterInputs.emit();
        this.mediaIDGrid?.setData([]);
        this.selectedDataAreaPaths.length = 0;
        this.custodiansAndMediaIDs.isBusy.custodian = this.custodiansAndMediaIDs.isBusy.mediaId = false;
        this.custodiansAndMediaIDs.items.custodian.length = this.custodiansAndMediaIDs.items.mediaId.length = 0;
    }

    refreshAll() {
        this.clearGrids();
        this.dataAreaNodes.length = 0;
        const remotePath = this.selectedConnector && this.selectedConnector.remotePath ? this.selectedConnector.remotePath : '';
        this.parentNodeName = remotePath.substring(remotePath.lastIndexOf('/') + 1);
        if (this.selectedConnector) {
            this.isConnectorSelected = true;
            const pathParam = { name: '', handle: '', type: 'UNKNOWN', leaf: false };
            this.nodes = [];
            this.loadPaths(pathParam);
        } else {
            this.isConnectorSelected = false;
        }
    }

    refreshCustodianMediaIdGrids(): void {
        if (this.dataAreaNodes && this.dataAreaNodes.length > 0) {
            this.selectedDataAreaPaths.length = 0;
            this.dataAreaNodes.forEach(node => {
                if (node.dataAreaPath && node.dataAreaPath.length && node.dataAreaPath.length > 0) {
                    this.selectedDataAreaPaths.push(node.dataAreaPath);
                }
            });
            this.selectedDataAreaPaths = _.unique(this.selectedDataAreaPaths);
            this.restService.post(Endpoint.projectManager_getIndexSettings, { handle: this.commonService.currentProject.handle }).then(response => {
                const promiseList = [];
                if (response?.settings?.custodianDirectoryLocation > 0 && this.projectCustodians.length === 0) {
                    promiseList.push(this.restService.post(Endpoint.ecaManager_listCustodians, { projectHandle: this.commonService.currentProject.handle }).then(responseCustodians => {
                        if (responseCustodians?.custodians?.length > 0) {
                            responseCustodians.custodians.forEach(el => {
                                this.projectCustodians.push(el.name);
                            })
                            this.addFilterItems(this.custodiansAndMediaIDs.fieldNames.custodian);
                        }
                    }))
                }
                if (response?.settings?.mediaDirectoryLocation > 0 && this.projectMediaIds.length === 0) {
                    promiseList.push(this.restService.post(Endpoint.ecaManager_listMediaids, { projectHandle: this.commonService.currentProject.handle, includeUnassignedMetadataView: false }).then(responseMediIds => {
                        if (responseMediIds?.mediaids?.length > 0) {
                            responseMediIds.mediaids.forEach(el => {
                                this.projectMediaIds.push(el.name);
                            })
                            this.addFilterItems(this.custodiansAndMediaIDs.fieldNames.mediaId);
                        }
                    }))
                }
                Promise.all(promiseList).then(() => {
                    const custodianRequestInfo = {
                        endpoint: Endpoint.connectorManager_previewCustodians,
                        data: {
                            connectorHandle: this.selectedConnector.handle,
                            custodianDirectoryLocation: response?.settings?.custodianDirectoryLocation ? response.settings.custodianDirectoryLocation : '0',
                            dataAreaPaths: this.selectedDataAreaPaths,
                            projectHandle: this.commonService.currentProject.handle
                        }
                    };
                    this.populateGrid(this.custodiansAndMediaIDs.fieldNames.custodian, custodianRequestInfo);

                    const mediaIDRequestInfo = {
                        endpoint: Endpoint.connectorManager_previewMediaIds,
                        data: {
                            connectorHandle: this.selectedConnector.handle,
                            dataAreaPaths: this.selectedDataAreaPaths,
                            mediaDirectoryLocation: response?.settings?.mediaDirectoryLocation ? response.settings.mediaDirectoryLocation : '0',
                            projectHandle: this.commonService.currentProject.handle
                        }
                    };
                    this.populateGrid(this.custodiansAndMediaIDs.fieldNames.mediaId, mediaIDRequestInfo);
                })
            });
        } else {
            this.clearGrids();
            this.custodianGrid.setData([]);
            this.mediaIDGrid.setData([]);
        }
    }

    resetCustodiansMediaIDsForGoogle(): void {
        this.googleCustodianGrid?.gridContext?.clearAllFilterInputs.emit();
        this.mediaIDGrid?.gridContext?.clearAllFilterInputs.emit();
        this.resetGrid(this.custodiansAndMediaIDs.fieldNames.custodian);
        this.resetGrid(this.custodiansAndMediaIDs.fieldNames.mediaId);
    }

    resetGrid(fieldName: string) {
        const grid = fieldName === this.custodiansAndMediaIDs.fieldNames.custodian ? this.googleCustodianGrid : this.mediaIDGrid;
        this.custodiansAndMediaIDs.items[fieldName].forEach(item => {
            item.newValue = item[fieldName]
        })
        grid.setData(this.custodiansAndMediaIDs.items[fieldName]);
        grid.gridApi?.forEachNode((node) => {
            if (!node.data.isDis) {
                node.setSelected(true);
            }
        });
    }

    addFilterItems(field) {
        const grid = field === this.custodiansAndMediaIDs.fieldNames.custodian ? this.isGoogleConnectorSelected ? this.googleCustodianGrid : this.custodianGrid : this.mediaIDGrid;
        const colDefs = field === this.custodiansAndMediaIDs.fieldNames.custodian ? this.isGoogleConnectorSelected ? this.googleCustodianColumnDefs : this.custodianColumnDefs : this.mediaIDColumnDefs;
        const projColDef = colDefs.find(f => f.field === 'newValue');
        if (projColDef) {
            projColDef.filterItems = field === this.custodiansAndMediaIDs.fieldNames.custodian ? this.projectCustodians : this.projectMediaIds;
            if (grid?.gridApi) {
                grid.gridApi.setGridOption('columnDefs', colDefs);
            }
        }
    }

    populateGrid(field, requestInfo) {
        this.custodiansAndMediaIDs.isBusy[field] = true;
        const grid = field === this.custodiansAndMediaIDs.fieldNames.custodian ? this.isGoogleConnectorSelected ? this.googleCustodianGrid : this.custodianGrid : this.mediaIDGrid;
        grid?.gridContext?.clearAllFilterInputs.emit();
        this.restService.post(requestInfo.endpoint, requestInfo.data).then(response => {
            this.custodiansAndMediaIDs.items[field] = [];
            if (response?.entityNames?.length > 0) {
                response.entityNames.forEach(el => {
                    const tempCustodian = {
                        newValue: el,
                        popoverError: {
                            isError: false,
                            popover: false,
                            message: '',
                            type: 'error'
                        }
                    };
                    tempCustodian[field] = el;
                    this.custodiansAndMediaIDs.items[field].push(tempCustodian);
                });
            }
            grid.setData(this.custodiansAndMediaIDs.items[field]);
            this.custodiansAndMediaIDs.isBusy[field] = false;
        })
    }

    onFilterApply(queryStr, filterType, headerName, fieldName) {
        const grid = fieldName === this.custodiansAndMediaIDs.fieldNames.custodian ? this.isGoogleConnectorSelected ? this.googleCustodianGrid : this.custodianGrid : this.mediaIDGrid;
        grid.onFilterApply(queryStr, filterType, headerName);
        if (grid.filters?.length > 0) {
            var filteredItems = cloneDeep(this.custodiansAndMediaIDs.items[fieldName]);
            grid.filters.forEach(filter => {
                if (filter.filterColumn.includes('Source')) {
                    const name = fieldName === this.custodiansAndMediaIDs.fieldNames.custodian ?
                        (this.isGoogleConnectorSelected ? 'sourceAccount' : fieldName) :
                        (this.isGoogleConnectorSelected ? 'dataSource' : fieldName)
                    filteredItems = _.filter(filteredItems, (field) => field[name] ? field[name].toLowerCase().indexOf(filter.value.toLowerCase()) !== -1 : false);
                } else {
                    filteredItems = _.filter(filteredItems, (field) => field.newValue && !field.isDis ? field.newValue.toLowerCase().indexOf(filter.value.toLowerCase()) !== -1 : false);
                }
            })
            grid.setData(filteredItems);
        } else {
            grid.setData(this.custodiansAndMediaIDs.items[fieldName]);
        }
    }

    loadNodeDataCallback = (node: GenericTreeNode) => {
        if (node) {
            this.loadPaths(node.data.handle, node);
        }
    }

    isSelectedNodeComplete(): boolean {
        let complete = false;
        if (!isUndefined(this.tree) && !isUndefined(this.tree.selectedNodes) && !isUndefined(this.tree.selectedNodes.length) && (this.tree.selectedNodes.length > 0)) {
            if (this.selectedConnector.type === ConnectorType.GoogleWorkspace.toString()) {
                complete = this.custodiansAndMediaIDs.items?.mediaId?.length > 0 && this.custodiansAndMediaIDs.items?.custodian?.length > 0;
            } else {
                complete = true;
            }
        }
        return complete;
    }

    isPrimaryButtonEnabled() {
        let enabled = false;
        if (!DrObject.isEmpty(this.menuComponent.nameDescriptionComponent.name)) {
            if (this.menuComponent.nameDescriptionComponent.isNameDirty() && (this.menuComponent.nameDescriptionComponent.formValid) && this.isSelectedNodeComplete()) {
                enabled = true;
            }

            if ((enabled === false) && (this.menuComponent.nameDescriptionComponent.isDescriptionDirty()) && (this.menuComponent.nameDescriptionComponent.formValid) && this.isSelectedNodeComplete()) {
                enabled = true;
            }

            // TODO: there should be work to determine if the selected node is dirty see isTargetPickerDirty() in comparison
            if (!this.menuComponent.nameDescriptionComponent.isDescriptionDirty() && !this.menuComponent.nameDescriptionComponent.isNameDirty() && this.isSelectedNodeComplete()) {
                enabled = true;
            }
        }
        return enabled && !this.isBusy && !this.custodiansAndMediaIDs.isError.custodian && !this.custodiansAndMediaIDs.isError.mediaId;
    }

    ok() {
        let count = 0;
        this.isBusy = true;
        const dataAreaHandles = [];
        const projectHandle = this.commonService.projectData.handle;
        let custodianMappings = {},
            mediaidMappings = {},
            custodianChanged = false,
            mediaIdChanged = false;

        this.custodiansAndMediaIDs.items.custodian.forEach(el => {
            el.newValue = el.newValue.trim();
            if (this.isGoogleConnectorSelected) {
                if (!el.isDis) {
                    custodianMappings[el.sourceAccount] = el.newValue
                }
            } else if (el.custodian !== el.newValue) {
                custodianChanged = true;
                custodianMappings[el.custodian] = el.newValue;
            }
        })
        this.custodiansAndMediaIDs.items.mediaId.forEach(el => {
            el.newValue = el.newValue.trim();
            if (this.isGoogleConnectorSelected) {
                mediaidMappings[el.sourceAccount + '|' + el.dataSource] = el.newValue
            } else if (el.mediaId !== el.newValue) {
                mediaIdChanged = true;
                mediaidMappings[el.mediaId] = el.newValue;
            }
        })

        let dataSources = []
        this.tree.selectedNodes.forEach(node => {
            dataSources.push(node.name);
        })
        // call createDataArea api for each data-area
        this.menuComponent.nameDescriptionComponent.validate().then(valid => {
            if (valid) {
                this.dataAreaNodes.forEach(dataArea => {
                    this.restService.post(Endpoint.orgManager_createDataArea, {
                        connectorHandle: this.selectedConnector.handle,
                        description: this.menuComponent.nameDescriptionComponent.description || '',
                        mode: 'IMPORT',
                        name: this.menuComponent.nameDescriptionComponent.name + '_' + dataArea.name,
                        path: dataArea.dataAreaPath || '.',
                        skippedDirectories: dataArea.skippedDirectories
                    }).then(createDataAreaResponse => {
                        dataAreaHandles.push(createDataAreaResponse.dataArea.handle);
                        count++; // count the number of times createDataArea is getting called
                        if (count === this.dataAreaNodes.length) {
                            this.restService.post(Endpoint.orgManager_createCorpus, {
                                attributes: [{ name: 'projecthandle', value: this.commonService.currentProject.handle }],
                                brand: true,
                                dataAreaHandles: dataAreaHandles,
                                description: this.menuComponent.nameDescriptionComponent.description,
                                name: this.menuComponent.nameDescriptionComponent.name,
                                loadFileName: '',
                                loadFileType: LoadFileType.EDRM_XML,
                                loadFileProfileId: -1
                            }).then(createCorpusResponse => {
                                this.closeModal(true, { name: this.menuComponent.nameDescriptionComponent.name, entityType: UpdateStatusType.CORPUS });

                                const sourceCorpus = createCorpusResponse.corpus;
                                let scanAttributes = this.menuComponent.setAttributes;
                                if (!scanAttributes) {
                                    scanAttributes = [];
                                }
                                scanAttributes.push({
                                    name: 'batchNumber', value: this.menuComponent.batchName?.trim()?.length > 0 ?
                                        this.menuComponent.batchName.trim() : this.menuComponent.nameDescriptionComponent.name
                                });
                                scanAttributes.push({ name: 'projecthandle', value: this.commonService.currentProject.handle });
                                if (this.modalData.shortMessageFormat) {
                                    scanAttributes.push({ name: 'shortMessageFormat', value: 'true' });
                                    scanAttributes.push({ name: 'shortMesageType', value: this.menuComponent.smfType})
                                }
                                if (this.selectedConnector?.type === "Exchange") {
                                    scanAttributes.push({
                                        name: 'startTime', value: this.menuComponent.dateObject.startDate ? Math.floor(this.menuComponent.dateObject.startDate.getTime()) + "" : null
                                    });
                                    scanAttributes.push({
                                        name: 'endTime', value: this.menuComponent.dateObject.endDate ? Math.floor(this.menuComponent.dateObject.endDate.getTime()) + "" : null
                                    });
                                }
                                if (this.selectedConnector?.type === ConnectorType.GoogleWorkspace.toString()) {
                                    const startDate = this.menuComponent.dateObject.startDate,
                                        endDate = this.menuComponent.dateObject.endDate;
                                    scanAttributes.push({
                                        name: 'startTime', value: this.menuComponent.dateObject.startDate ? Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()) + "" : null
                                    });
                                    scanAttributes.push({
                                        name: 'endTime', value: this.menuComponent.dateObject.endDate ? Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59) + "" : null
                                    });
                                    scanAttributes.push({
                                        name: 'searchTerms', value: this.menuComponent.dateObject.searchTerms
                                    });
                                }

                                this.restService.post(Endpoint.projectManager_listCorpusSets, {
                                    projectHandle: this.commonService.currentProject.handle,
                                    count: 1,
                                    startIndex: 0
                                }).then(listCorpusSetsResponse => {
                                    this.restService.post(Endpoint.corpusSetManager_addCorpus, {
                                        corpusHandle: createCorpusResponse.corpus.handle,
                                        corpusSetHandle: listCorpusSetsResponse.corpusSets && listCorpusSetsResponse.corpusSets.length > 0 ? listCorpusSetsResponse.corpusSets[0].handle : null
                                    }).then(() => {
                                        const indexSettings = this.menuComponent.getIndexLevelSetting();
                                        let requestData: any = {
                                            attributes: [{ name: 'projecthandle', value: this.commonService.currentProject.handle }],
                                            corpusHandle: createCorpusResponse.corpus.handle,
                                            scanAttributes: scanAttributes ? scanAttributes : null,
                                            taskDescription: 'Creating representation ' + indexSettings.name + ' for ' + this.menuComponent.nameDescriptionComponent.name,
                                            typeList: indexSettings.indexLevelSetting,
                                            enablePatternDetection: this.menuComponent.isPatternDetectionEnabled,
                                        }
                                        if (this.isGoogleConnectorSelected) {
                                            requestData.parsingCustomizations = {
                                                custodianMappings: custodianMappings,
                                                mediaidMappings: mediaidMappings,
                                                dataSources: dataSources
                                            }
                                        } else {
                                            if (custodianChanged) {
                                                requestData.parsingCustomizations = {
                                                    custodianMappings: custodianMappings
                                                }
                                            }
                                            if (mediaIdChanged) {
                                                if (requestData.parsingCustomizations) {
                                                    requestData.parsingCustomizations.mediaidMappings = mediaidMappings
                                                } else {
                                                    requestData.parsingCustomizations = {
                                                        mediaidMappings: mediaidMappings
                                                    }
                                                }
                                            }
                                        }
                                        this.restService.post(Endpoint.corpusManager_createRepresentation, requestData, true, true).then(representationResponse => {
                                            if (representationResponse && representationResponse.taskHandle && this.addToProjectData) {
                                                this.restService.post(Endpoint.viewManager_addFromView, {
                                                    filterSrcDuplicates: false,
                                                    includeFamilyMembers: false,
                                                    destViewHandle: projectHandle,
                                                    srcViewHandle: createCorpusResponse.corpus.corpusViewHandle,
                                                    taskDescription: 'Adding documents to Project Data',
                                                }).then(() => {
                                                    this.isBusy = false;
                                                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CORPUS);
                                                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CUSTODIAN);
                                                });
                                            } else {
                                                this.isBusy = false;
                                            }
                                        }, error => {
                                            this.isBusy = false;
                                        });
                                    }, () => {
                                        this.isBusy = false;
                                    });
                                });
                            }, error => {
                                this.isBusy = false;
                            });
                        }

                    });
                });
            } else {
                this.isBusy = false;
            }
        });
    }

    findParentDataArea(path, node) {
        if (!isUndefined(node.parent) && node.parent.data.isDataArea) {
            const parentHandle = node.parent.data.handle;

            // find data area in the existing dataAreaNodes
            const nodeInDataAreaNodes = _.findWhere(this.dataAreaNodes, { dataAreaPath: parentHandle });
            const index = this.dataAreaNodes.indexOf(nodeInDataAreaNodes);

            if (index !== -1) {
                return nodeInDataAreaNodes.dataAreaPath;
            }
        } else {
            for (let i = node.level; i >= 0; i--) {
                node = node.parent;
                if (node) {
                    const findDataArea = _.findWhere(this.dataAreaNodes, { dataAreaPath: node.data.handle });
                    const index = this.dataAreaNodes.indexOf(findDataArea);

                    if (index !== -1) {
                        return findDataArea.dataAreaPath;
                    }
                }
            }
        }
    }

    updateResizer() {
        this.treeLeftResizer.usePercentages = this.isGoogleConnectorSelected ? false : true;
        this.treeLeftResizer.initSize = this.isGoogleConnectorSelected ? 180 : 35;
        this.treeLeftResizer.minSize = this.isGoogleConnectorSelected ? 180 : 20;
        this.isGoogleConnectorSelected ? this.treeLeftResizer.setElementSize(180) : this.treeLeftResizer.setElementSize(35);
    }

    loadAccountsAndServices() {
        this.custodiansAndMediaIDs.isBusy.custodian = this.custodiansAndMediaIDs.isBusy.mediaId = true;
        let nodes = new Array<GenericTreeNode>();
        if (this.selectedConnector?.dataSources?.length > 0) {
            this.selectedConnector.dataSources.forEach(ds => {
                nodes.push(new GenericTreeNode({ 
                    name: ds, 
                    hasCheckbox: Boolean(this.checkboxSelectionChangedCallback), 
                    isExpandable: false, 
                    hasExpanderPadding: true, 
                    isSelectable: true
                 }))
            });
        }
        this.tree.setData(nodes);
        this.restService
            .post(Endpoint.connectorManager_exploreConnector, { connectorHandle: this.selectedConnector.handle, parentPath: { handle: '/Users', name: 'Users', type: 'UNKNOWN', leaf: false } })
            .then(response => {
                this.custodiansAndMediaIDs.items.custodian = [];
                const promiseList = [];
                if (response?.paths) {
                    if (this.projectCustodians.length === 0) {
                        promiseList.push(this.restService.post(Endpoint.ecaManager_listCustodians, { projectHandle: this.commonService.currentProject.handle }).then(responseCustodians => {
                            if (responseCustodians?.custodians?.length > 0) {
                                responseCustodians.custodians.forEach(el => {
                                    this.projectCustodians.push(el.name);
                                })
                                this.addFilterItems(this.custodiansAndMediaIDs.fieldNames.custodian);
                            }
                        }))
                    }
                    if (this.projectMediaIds.length === 0) {
                        promiseList.push(this.restService.post(Endpoint.ecaManager_listMediaids, { projectHandle: this.commonService.currentProject.handle, includeUnassignedMetadataView: false }).then(responseMediIds => {
                            if (responseMediIds?.mediaids?.length > 0) {
                                responseMediIds.mediaids.forEach(el => {
                                    this.projectMediaIds.push(el.name);
                                })
                                this.addFilterItems(this.custodiansAndMediaIDs.fieldNames.mediaId);
                            }
                        }))
                    }
                    Promise.all(promiseList).then(() => {
                        this.custodiansAndMediaIDs.items.custodian = [];
                        response.paths.forEach(path => {
                            this.custodiansAndMediaIDs.items.custodian.push({
                                sourceAccount: path.handle, custodian: path.name, newValue: path.name, isDis: true, popoverError: {
                                    isError: false,
                                    popover: false,
                                    message: '',
                                    type: 'error'
                                }
                            })
                        });
                        this.googleCustodianGrid?.setData(this.custodiansAndMediaIDs.items.custodian);
                        this.mediaIDGrid?.setData([]);
                    })
                };
                this.custodiansAndMediaIDs.isBusy.custodian = this.custodiansAndMediaIDs.isBusy.mediaId = false;
            });

        this.commonService.sendMessage(MessageType.connectorChanged, {
            connector: this.selectedConnector, paths: this.selectedDataAreaPaths,
        });
    }
    
    loadPaths(path, parent?: GenericTreeNode, filter?: any): void {
        let connectorFolderPath = {};
        if (parent) {
            connectorFolderPath = { handle: path, name: '', type: 'UNKNOWN', leaf: false };
            parent.isLoading = true;
        } else {
            connectorFolderPath = path;
            this.isPathsBusy = true;
        }

        const requestData: any = {
            connectorHandle: this.selectedConnector.handle,
            parentPath: connectorFolderPath
        };

        if (filter) {
            requestData.filters = [filter];
        }

        this.restService
            .post(Endpoint.connectorManager_exploreConnector, requestData)
            .then(response => {

                const isSearchMode = requestData.filters && requestData.filters.some(f => f.attribute === 'folder');

                if (response && response.paths && isSearchMode) {
                    this.buildTreeFromSearchResults(response.paths);
                    this.isPathsBusy = false;
                    return;
                }

                const paths = [];
                if (response && response.paths) {
                    // Update the dataAreaNodes
                    const findPathInDataAreaNode = _.findWhere(this.dataAreaNodes, { dataAreaPath: path });
                    const indexInDataAreaNodes = this.dataAreaNodes.indexOf(findPathInDataAreaNode);

                    if (indexInDataAreaNodes !== -1) {
                        response.paths.forEach(descendant => {
                            if (descendant.name !== '.' && descendant.name !== '..' && findPathInDataAreaNode.allDescendants.indexOf(descendant.handle) === -1) {
                                findPathInDataAreaNode.allDescendants.push(descendant.handle);
                            }
                        });
                    } else if (parent) {
                        const getParentpath = this.findParentDataArea(path, parent);
                        if (getParentpath && getParentpath.length > 0) {
                            const findDataArea = _.findWhere(this.dataAreaNodes, { dataAreaPath: getParentpath });
                            const index = this.dataAreaNodes.indexOf(findDataArea);

                            response.paths.forEach(descendant => {
                                if (descendant.name !== '.' && descendant.name !== '..' && findDataArea &&
                                    findDataArea.allDescendants && findDataArea.allDescendants.indexOf(descendant.handle) === -1) {
                                    const allDescendants = findDataArea.allDescendants;
                                    if (allDescendants.indexOf(descendant.handle) === -1) {
                                        allDescendants.push(descendant.handle);
                                    }
                                }
                            });
                        }
                    }
                    response.paths.forEach((element, index) => {
                        if (element.name !== '.' && element.name !== '..') {
                            paths.push(new GenericTreeNode({
                                name: element.name, icon: 'fa-folder', iconExpanded: 'fa-folder-open', iconColor: 'color-yellow-07',
                                isExpandable: !element.leaf, isExpandedOnSelect: !element.leaf, automationId: element.name + '-' + index,
                                hasExpanderPadding: true, data: element, hasCheckbox: Boolean(this.checkboxSelectionChangedCallback)
                            }));
                        }
                    });
                }

                if (parent) {
                    this.tree.setNodeData(parent, paths);
                    if (parent.children.length === 0) {
                        parent.isExpandable = false;
                        parent.iconExpanded = null;
                    }
                    parent.isLoading = false;
                } else {
                    parent = new GenericTreeNode({
                        name: this.parentNodeName, isExpandable: true, isExpandedOnSelect: true, isExpanded: true,
                        automationId: this.parentNodeName,
                        hasExpanderPadding: true, data: { name: '', handle: '.', type: 'UNKNOWN', leaf: false }, hasCheckbox: Boolean(this.checkboxSelectionChangedCallback)
                    });
                    this.nodes = [parent];
                    this.tree.setData(this.nodes);
                    this.tree.setNodeData(parent, paths);
                    this.updateResizer();
                    this.isPathsBusy = false;
                }
            }, error => {
                if (parent) {
                    parent.isLoading = false;
                } else {
                    this.isPathsBusy = false;
                }
            });
    }

    filterData(str: string): void {
        this.debounceFilter(str);
    }

    filterAfterBounce(str: string): void {
        if (this.selectedConnector) {
            if (str && str.trim()) {
                if (str?.length >= 2) {
                    this.nodes = [];
                    const newFilter = { attribute: 'folder', value: str, operator: 'LIKE' };
                    this.isFiltering = true;
                    const pathParam = { name: '', handle: '', type: 'UNKNOWN', leaf: false };
                    this.nodes = [];
                    this.loadPaths(pathParam, null, newFilter);
                }
            } else {
                this.onClearSearch();
            }
        }
    }

    onClearSearch(): void {
        this.isFiltering = false;
        if (this.selectedConnector) {
            this.custodianGrid?.setData([]);
            this.mediaIDGrid?.setData([]);
            const pathParam = { name: '', handle: '', type: 'UNKNOWN', leaf: false };
            this.loadPaths(pathParam, null, null);
        }
    }

    buildTreeFromSearchResults(paths: any[]): void {
        let rootNode: GenericTreeNode;
        const existingPathMap = new Map<string, GenericTreeNode>();

        if (this.nodes && this.nodes.length > 0) {
            rootNode = this.nodes[0];
            this.buildPathMapFromTree(rootNode, existingPathMap);
        } else {
            rootNode = new GenericTreeNode({
                name: this.parentNodeName,
                isExpandable: true,
                isExpandedOnSelect: true,
                isExpanded: true,
                automationId: this.parentNodeName,
                hasExpanderPadding: true,
                data: { name: '', handle: '.', type: 'UNKNOWN', leaf: false },
                hasCheckbox: Boolean(this.checkboxSelectionChangedCallback)
            });
            this.nodes = [rootNode];
            this.tree.setData(this.nodes);
        }

        paths.sort((a, b) => {
            const depthA = (a.handle.match(/\//g) || []).length;
            const depthB = (b.handle.match(/\//g) || []).length;
            return depthA - depthB;
        });

        paths.forEach((pathData, index) => {
            if (pathData.name === '.' || pathData.name === '..') {
                return;
            }

            if (existingPathMap.has(pathData.handle)) {
                return;
            }

            const node = new GenericTreeNode({
                name: pathData.name,
                icon: 'fa-folder',
                iconExpanded: 'fa-folder-open',
                iconColor: 'color-yellow-07',
                isExpandable: !pathData.leaf,
                isExpandedOnSelect: false,
                automationId: pathData.name + '-' + index,
                hasExpanderPadding: true,
                data: pathData,
                hasCheckbox: Boolean(this.checkboxSelectionChangedCallback)
            });

            existingPathMap.set(pathData.handle, node);

            const parentPath = pathData.handle.substring(0, pathData.handle.lastIndexOf('/'));
            let parentNode: GenericTreeNode;

            if (parentPath && existingPathMap.has(parentPath)) {
                parentNode = existingPathMap.get(parentPath);
            } else {
                parentNode = rootNode;
            }

            if (!parentNode.children) {
                parentNode.children = [];
            }
            parentNode.children.push(node);
            node.parent = parentNode;

            // Expand parent nodes so search results are visible
            this.expandParentChain(parentNode);
        });

        this.tree.setNodeData(rootNode, rootNode.children || []);
        this.tree.treeDataUpdated();
    }

    buildPathMapFromTree(node: GenericTreeNode, pathMap: Map<string, GenericTreeNode>): void {
        if (node.data && node.data.handle) {
            pathMap.set(node.data.handle, node);
        }
        if (node.children) {
            node.children.forEach(child => this.buildPathMapFromTree(child, pathMap));
        }
    }

    expandParentChain(node: GenericTreeNode): void {
        let current = node;
        while (current && current.parent) {
            if (!current.isExpanded && current.isExpandable) {
                current.isExpanded = true;
            }
            current = current.parent;
        }
    }
}